import { FormArray } from '@angular/forms';
import {
  CompanyDirectorInput,
  CompanyShareholderInput,
  OfficerCompanyInput,
  OfficerCompanyType,
  OfficerPersonInput,
  OfficerPersonType,
  OfficerType,
  OfficerTypeEnum,
  SalutationEnum,
  SupportedCountryType,
} from '@generated/graphql';
import { AbstractOfficerForm } from '@shared/classes/officer/abstract-officer-form.class';
import { createOfficerForm } from '@shared/classes/officer/officer-form-factory';
import { OfficerInputHelper } from '@shared/helpers/officer-input.helper';
import { OfficerHelper } from '@shared/helpers/officer.helper';
import { OfficerRoleEnum } from '@shared/types/officerEnum';
import { sortBy, uniqBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { ExistsOfficerType } from './exists-officer.type';

export type OfficerDialogMode = 'new' | 'edit';

export abstract class OfficerDialogState {
  mode: OfficerDialogMode;

  officerRole: OfficerRoleEnum;

  title: string;

  informationLabel: string;

  selectedOfficerType: OfficerTypeEnum;

  officerFormState: AbstractOfficerForm;

  existingOfficers: ExistsOfficerType[] = [];

  countryOfIncorporation: SupportedCountryType;

  private readonly officerHelper = new OfficerHelper();

  constructor(officerRole: OfficerRoleEnum, mode: OfficerDialogMode) {
    this.officerRole = officerRole;

    this.mode = mode;
    this.setTitle();
    this.setInformationLabel();
    this.setSelectedOfficerType(OfficerTypeEnum.Individual);
  }

  resetOfficerForm(): this {
    this.officerFormState.officerForm = undefined;

    return this;
  }

  isDirector(): boolean {
    return this.officerRole === OfficerRoleEnum.DIRECTOR;
  }

  isShareholder(): boolean {
    return this.officerRole === OfficerRoleEnum.SHAREHOLDER;
  }

  setSelectedOfficerType(officerType: OfficerTypeEnum): this {
    this.selectedOfficerType = officerType;

    return this;
  }

  createOfficerForm(): this {
    let shareDistributionForm = undefined;

    if (this.officerFormState?.shareDistributionForm) {
      shareDistributionForm = this.officerFormState.shareDistributionForm;
    }

    this.officerFormState = createOfficerForm(this.selectedOfficerType);
    this.officerFormState.createOfficerForm();

    if (!shareDistributionForm) {
      this.officerFormState.createShareDistributionsForm();
    } else {
      this.officerFormState.shareDistributionForm = shareDistributionForm;
    }

    return this;
  }

  getOfficerForm() {
    return this.officerFormState.officerForm;
  }

  disableOfficerFormForExistingReference(): this {
    Promise.resolve().then(() => {
      this.getOfficerForm().disable();
    });

    return this;
  }

  /**
   * Set officers from current job
   */
  setCurrentJobOfficers(officers: FormArray): this {
    if (officers && officers.length) {
      const normalOfficers = officers
        .getRawValue()
        .filter((o) => !o.refId || !o.refId.includes('temporary-'))
        .map((o) => ({
          id: o.id,
          name: OfficerInputHelper.getOfficerName(
            o.officerCompany || o.officerPerson,
          ),
          email: OfficerInputHelper.getOfficerEmail(
            o.officerPerson || o.officerCompany,
          ),
          type: o.officerType as OfficerTypeEnum,
          refId: o.refId,
        }));

      const representative = officers
        .getRawValue()
        .filter((o) => o.officerType === OfficerTypeEnum.Corporate)
        .map((o) => ({
          id: o.officerCompany.representative.id,
          name: OfficerInputHelper.getOfficerName(
            o.officerCompany.representative,
          ),
          email: OfficerInputHelper.getOfficerEmail(
            o.officerCompany.representative,
          ),
          type: OfficerTypeEnum.Individual,
          refId: o.officerCompany.representative.id,
        }));

      this.existingOfficers = [
        ...this.existingOfficers,
        ...normalOfficers,
        ...representative,
      ];

      this.normalizeExistingOfficers();
    }

    return this;
  }

  /**
   * Set officers from current customer's company
   */
  setExistingOfficers(officers: OfficerType[]): this {
    if (officers.length) {
      const normalOfficers = officers.map((o) => ({
        id: o.id,
        name: this.officerHelper.getOfficerName(o.officerProfile),
        email: this.officerHelper.getOfficerEmail(o.officerProfile),
        type:
          o.officerProfile.__typename === 'OfficerCompanyType'
            ? OfficerTypeEnum.Corporate
            : OfficerTypeEnum.Individual,
        refId: o.id,
      }));

      const representative = officers
        .filter((o) => o.officerProfile.__typename === 'OfficerCompanyType')
        .map((o) => {
          const representative = (o.officerProfile as OfficerCompanyType)
            .representative;
          return {
            id: representative.id,
            name: this.officerHelper.getOfficerName(
              representative.officerProfile,
            ),
            email: this.officerHelper.getOfficerEmail(
              representative.officerProfile,
            ),
            type: OfficerTypeEnum.Individual,
            refId: representative.id,
          };
        });

      this.existingOfficers = [
        ...this.existingOfficers,
        ...normalOfficers,
        ...representative,
      ];

      this.normalizeExistingOfficers();
    }

    return this;
  }

  normalizeExistingOfficers(): this {
    if (
      this.countryOfIncorporation.country.code.toUpperCase() === 'SG' &&
      this.officerRole === OfficerRoleEnum.DIRECTOR
    ) {
      this.existingOfficers = this.existingOfficers.filter(
        (e) => e.type !== OfficerTypeEnum.Corporate,
      );
    }

    this.existingOfficers = sortBy(
      uniqBy(this.existingOfficers, (o) => o.id),
      (o) => o.name,
    );

    return this;
  }

  mapOfficerPersonInput(officerProfile: OfficerPersonType): OfficerPersonInput {
    const officerProfileInput: OfficerPersonInput = {
      address: officerProfile.address.address,
      apartment: officerProfile.address.apartment,
      city: officerProfile.address.city,
      countryId: officerProfile.address.country.id,
      phone: officerProfile.address.phone,
      state: officerProfile.address.state,
      zip: officerProfile.address.zip,
      email: officerProfile.email,
      firstName: officerProfile.firstName,
      lastName: officerProfile.lastName,
      salutation: officerProfile.salutation as SalutationEnum,
      identification: {
        expiryDate: DateTime.fromISO(
          officerProfile.identification.expiryDate,
        ).toJSDate(),
        identificationNumber:
          officerProfile.identification.identificationNumber,
        nationalityId: officerProfile.identification.nationality.id,
        officerIdentificationTypeId:
          officerProfile.identification.officerIdentificationType.id,
      },
    };

    return officerProfileInput;
  }

  mapOfficerCompanyInput(officerProfile: OfficerCompanyType): OfficerCompanyInput {
    const officerProfileInput: OfficerCompanyInput = {
      address: {
        address: officerProfile.address.address,
        apartment: officerProfile.address.apartment,
        city: officerProfile.address.city,
        countryId: officerProfile.address.country.id,
        phone: officerProfile.address.phone,
        state: officerProfile.address.state,
        zip: officerProfile.address.zip,
      },
      countryId: officerProfile.country.id,
      email: officerProfile.email,
      name: officerProfile.name,
      registrationDate: DateTime.fromISO(
        officerProfile.registrationDate,
      ).toJSDate(),
      registrationNumber: officerProfile.registrationNumber,
      website: officerProfile.website,
      representative: {
        id: officerProfile.representative.id,
        ...this.mapOfficerPersonInput(
          officerProfile.representative.officerProfile as OfficerPersonType,
        ),
      },
    };

    return officerProfileInput;
  }

  abstract setLookupData(officer: OfficerType, type: OfficerTypeEnum): this;

  abstract isOfficerFormValid(): boolean;

  abstract setTitle(): this;

  abstract setInformationLabel(): this;

  abstract prepareEditData(
    data: CompanyDirectorInput | CompanyShareholderInput,
  ): this;
}
